import { Tooltip } from 'antd'
import { type FC, type MouseEvent, useCallback, useMemo, useState } from 'react'

import {
  type AwayDescription,
  type OverlapBand,
  describeAwayOverlap,
  getAwayOverlaps,
  overlapBands,
  siteSecondsOfInstant,
  visitSeconds,
} from './awayOverlap'
import styles from './eventTooltip.module.scss'
import { resolveScheduleRoomId } from './helpers'
import { useResourceAway } from './resourceAwayStore'

type EventAway = {
  lines: AwayDescription[]
  bands: OverlapBand[]
}

const NO_AWAY: EventAway = { lines: [], bands: [] }

type AwayEvent = {
  room_id?: unknown
  start_time?: Date | string
  end_time?: Date | string
}

// A placed card's lunch/away overlap; its times are real instants read on the site clock.
export const useEventAway = (
  event: AwayEvent | undefined,
  timeZone: string | undefined,
  instanceKey?: string,
): EventAway => {
  const away = useResourceAway(resolveScheduleRoomId(event?.room_id), instanceKey)
  const { start_time: startTime, end_time: endTime } = event ?? {}

  return useMemo(() => {
    if (!away || !timeZone || !startTime || !endTime) return NO_AWAY
    const { start, end } = visitSeconds(
      siteSecondsOfInstant(startTime, timeZone),
      siteSecondsOfInstant(endTime, timeZone),
    )
    const overlaps = getAwayOverlaps(away.blocks, start, end)
    if (!overlaps.length) return NO_AWAY
    return {
      lines: overlaps.map((item) => describeAwayOverlap(item, away.staffLabel)),
      bands: overlapBands(overlaps, start, end),
    }
  }, [away, timeZone, startTime, endTime])
}

export const AwayTooltipLines: FC<{ lines: AwayDescription[] }> = ({ lines }) => (
  <>
    {lines.map((line) => (
      <div key={`${line.title}|${line.interval}`} className={styles.tooltipAway}>
        <div className={styles.tooltipTitle}>{line.title}</div>
        <div>{line.interval}</div>
        <div>{line.overlap}</div>
      </div>
    ))}
  </>
)

export const awayBandClassName = (band: OverlapBand): string =>
  `event-away-overlap event-away-overlap--${band.status}`

const NAME_SELECTOR = '.event-name-with-staff'

type BandHover = {
  hoveredBand: number | null
  onMouseMove: (event: MouseEvent<HTMLElement>) => void
  // Also closes on mouse down, so a drag never carries the card's tooltip along.
  onMouseLeave: () => void
}

// Bands sit under the card text, so the card itself reports which band the pointer is over.
export const useBandHover = (bands: OverlapBand[]): BandHover => {
  const [hoveredBand, setHoveredBand] = useState<number | null>(null)

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!bands.length) return
      // The name line keeps its own tooltip, which already lists the away time.
      const onName = (event.target as Element | null)?.closest?.(NAME_SELECTOR)
      const rect = event.currentTarget.getBoundingClientRect()
      const percent = rect.height ? ((event.clientY - rect.top) / rect.height) * 100 : -1
      const index = onName
        ? -1
        : bands.findIndex((band) => percent >= band.top && percent < band.top + band.height)
      setHoveredBand(index >= 0 ? index : null)
    },
    [bands],
  )

  const onMouseLeave = useCallback(() => setHoveredBand(null), [])

  return { hoveredBand, onMouseMove, onMouseLeave }

}

type AwayBandsProps = {
  bands: OverlapBand[]
  lines: AwayDescription[]
  hoveredBand: number | null
}

export const AwayBands: FC<AwayBandsProps> = ({ bands, lines, hoveredBand }) => (
  <>
    {bands.map((band, index) => (
      <Tooltip
        key={`${band.status}-${band.top}`}
        open={hoveredBand === index}
        placement='right'
        title={<AwayTooltipLines lines={lines[index] ? [lines[index]] : []} />}>
        <span
          aria-hidden
          className={awayBandClassName(band)}
          style={{ top: `${band.top}%`, height: `${band.height}%` }}
        />
      </Tooltip>
    ))}
  </>
)
